import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PatientLab } from '@/types/patient-chart-api';

/**
 * Patient labs list — Chart > Labs tab (PROD-738 PR A).
 *
 * Wire (Flutter `patient_chart_data_source_impl.dart:1141`):
 *
 *   GET /api/Patients/GetLabOrderByPatientId?PatientId={id}
 *       → PatientLab[]
 *
 * The endpoint name says "LabOrder" but returns ALL ordered diagnostics
 * (laboratory + radiology) — sloppy backend naming, the wire field
 * `radiologyOrderRequest` similarly covers both. Don't normalise; the
 * Flutter source uses the same names verbatim.
 *
 * Flutter wraps a 404 as an empty list — we let React Query's default
 * handling surface it via the global error toast instead, since dev
 * always returns 200 + an array (empty or populated). If a future
 * backend change reintroduces the 404-on-empty pattern, add an
 * `onError` here that swallows 404 and returns `[]`.
 */

export function usePatientLabs(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ['patient-labs', patientId],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ PatientId: patientId ?? '' });
      return get<PatientLab[]>(
        `/api/Patients/GetLabOrderByPatientId?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!patientId,
    staleTime: 60_000,
  });
}
